import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { localeFor, formatClockTime } from '../utils/timeFormat';
import { railsToIana, getTzAbbr } from '../utils/timezoneUtils';
import './ExtraRaceModal.css';

// Дополнительный заезд этапа (RMC): участник сам создает в ROUVY гонку по
// маршруту этапа в любой день недели этапа и прикладывает ссылку на нее.
// Результат идет в зачет этапа наравне с гонками расписания, если гонка
// проехана в неделю этапа — POST /api/events/:id/extra_races (ExtraRacesController)
const stopBubbling = (e) => e.stopPropagation();

const ExtraRaceModal = ({ isOpen, onClose, eventId, routeId, routeName, stageWeek, onAdded }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [link, setLink] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Окно переиспользуется — каждое открытие начинается с чистой формы
  useEffect(() => {
    if (!isOpen) return;
    setLink('');
    setError(null);
    setSuccess(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKey);

    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Неделя этапа считается по CET, а показываем ее в поясе участника из профиля
  // (как расписание RMC). Границы тогда не всегда приходятся на полночь, поэтому
  // кроме дня печатаем и время
  const timeZone = railsToIana(user?.time_zone);

  const formatWeekBoundary = (dateString) => {
    const date = new Date(dateString);
    const day = date.toLocaleDateString(
      localeFor(language, 'en-GB'),
      { timeZone, weekday: 'short', day: 'numeric', month: 'long' }
    );
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(date);
    const part = (type) => Number(parts.find(p => p.type === type)?.value);

    return `${day}, ${formatClockTime(part('hour'), part('minute'), language)}`;
  };

  // Пояс подписываем по концу недели; если внутри нее переход на летнее время — у обеих границ
  const weekRangeText = () => {
    const fromZone = getTzAbbr(timeZone, new Date(stageWeek.starts_at));
    const toZone = getTzAbbr(timeZone, new Date(stageWeek.ends_at));
    const from = formatWeekBoundary(stageWeek.starts_at);
    const to = formatWeekBoundary(stageWeek.ends_at);

    return fromZone === toZone
      ? `${from} — ${to} ${toZone}`
      : `${from} ${fromZone} — ${to} ${toZone}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!link.trim()) {
      setError(t('route_card.extra_race.error_url'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`/api/events/${eventId}/extra_races`, { route_id: routeId, event_link: link.trim() });
      setSuccess(true);
      if (onAdded) onAdded();
    } catch (err) {
      setError(err.response?.data?.error || t('route_card.extra_race.error_submit'));
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    // Карточка маршрута целиком ссылка на ROUVY: клики дальше модалки не пропускаем
    <div
      className="extra-race-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onMouseDown={stopBubbling}
      onMouseUp={stopBubbling}
      onTouchStart={stopBubbling}
      onTouchEnd={stopBubbling}
      onKeyDown={stopBubbling}
      onKeyPress={stopBubbling}
    >
      <form className="extra-race-modal" onClick={stopBubbling} onSubmit={handleSubmit}>
        <div className="extra-race-header">
          <div>
            <h3 className="extra-race-title">{t('route_card.extra_race.title')}</h3>
            {routeName && <div className="extra-race-subtitle">{routeName}</div>}
          </div>
          <button type="button" className="extra-race-close" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </div>

        <div className="extra-race-body">
          {success ? (
            <p className="extra-race-success">{t('route_card.extra_race.success')}</p>
          ) : (
            <>
              <p className="extra-race-hint">{t('route_card.extra_race.how_to')}</p>
              {stageWeek && (
                <div className="extra-race-week">
                  <span className="extra-race-week-label">{t('route_card.extra_race.week_label')}</span>
                  <span className="extra-race-week-range">{weekRangeText()}</span>
                </div>
              )}
              <label className="extra-race-field">
                <span>{t('route_card.extra_race.url_label')}</span>
                <input
                  type="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://riders.rouvy.com/events/3b45c007-..."
                  autoFocus
                />
              </label>
              {error && <p className="extra-race-error">{error}</p>}
            </>
          )}
        </div>

        <div className="extra-race-footer">
          {!success && (
            <button type="submit" className="extra-race-btn extra-race-btn-submit" disabled={submitting}>
              {submitting ? t('common.saving') : t('route_card.extra_race.submit')}
            </button>
          )}
          <button type="button" className="extra-race-btn extra-race-btn-cancel" onClick={onClose}>
            {success ? t('common.close') : t('common.cancel')}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default ExtraRaceModal;
